import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataTableSkeleton,
  DataType,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  makeStyles,
  useTheme
} from '@material-ui/core'
import { Check } from '@material-ui/icons'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { AccountTableFormat } from '../../../../api/Argentia/model/subjects'
import { getCustomerDomain } from '../../../../redux'
import { styles } from '../../contracts.styles'

type AccountsTableDialogProps = {
  accounts?: AccountTableFormat[]
  show: boolean
  onHandleClick: (account: AccountTableFormat | null) => void
  paymentMethodOptions: SelectOption[]
  billingCycleOptions: SelectOption[]
}

export const AccountsTableDialog: React.FC<AccountsTableDialogProps> = ({
  show,
  accounts,
  onHandleClick,
  paymentMethodOptions = [],
  billingCycleOptions = []
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'contracts', 'enums'])

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const { info } = useNotify()

  const handleCancel = () => {
    onHandleClick(null)
  }

  const handleConfirm = (account: AccountTableFormat | null) => {
    onHandleClick(account)
  }

  const accountsColumns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML,
      columnWidth: 3
    },
    {
      name: 'code',
      label: t('contracts:components.clientAccountsTable.columns.code'),
      type: DataType.TEXT
    },
    {
      name: 'paymentMethodType',
      label: t('contracts:components.clientAccountsTable.columns.paymentMethodType'),
      type: DataType.SELECT,
      selectOptions: paymentMethodOptions
    },
    {
      name: 'iban',
      label: t('contracts:components.clientAccountsTable.columns.iban'),
      type: DataType.TEXT
    },
    {
      name: 'postalAddress',
      label: t('contracts:components.clientAccountsTable.columns.postalAddress'),
      type: DataType.TEXT
    },
    {
      name: 'postalCode',
      label: t('contracts:components.clientAccountsTable.columns.postalCode'),
      type: DataType.TEXT
    },
    {
      name: 'municipality',
      label: t('contracts:components.clientAccountsTable.columns.municipality'),
      type: DataType.TEXT
    },
    {
      name: 'province',
      label: t('contracts:components.clientAccountsTable.columns.province'),
      type: DataType.TEXT
    },
    {
      name: 'country',
      label: t('contracts:components.clientAccountsTable.columns.country'),
      type: DataType.TEXT
    },
    {
      name: 'billingCycleId',
      label: t('contracts:components.clientAccountsTable.columns.billingCycle'),
      type: DataType.SELECT,
      selectOptions: billingCycleOptions
    }
  ]

  const handleSelect = (account: AccountTableFormat | null) => {
    handleConfirm(account)
  }

  const dataTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    // search: !cardMode,
    // elevation: 0,
    // filterType: !cardMode ? FilterType.ADVANCED : undefined,
    // download: !cardMode,
    canEdit: false,
    canDelete: false,
    canInsert: false,
    selectableRows: false,
    customInsertEditModal: true,
    rowsPerPage: 5,
    rowsPerPageOptions: [5, 10, 20],
    analytics: {
      customer: getCustomerDomain(),
      view: t('contracts:components.clientAccountsTable.analytics')
    },
    rowCustomActions: [
      {
        icon: <Check htmlColor="green" fontSize="small" />,
        tooltip: t('contracts:components.clientAccountsTable.rowAction.select'),
        visibleIf: 'canSelect',
        onClick: async (row: AccountTableFormat) => {
          handleSelect(row)
        }
      }
    ]
  }

  return (
    <Dialog maxWidth={'md'} fullWidth={true} disableBackdropClick disableEscapeKeyDown open={show}>
      <DialogTitle
        disableTypography
        style={{ padding: currentTheme.spacing(4), paddingTop: currentTheme.spacing(3) }}
      >
        <Typography variant="h1" color="primary" noWrap={true}>
          {t('contracts:components.clientAccountsTable.title')}
        </Typography>
      </DialogTitle>
      <DialogContent style={{ padding: currentTheme.spacing(4), paddingTop: 0 }}>
        {!accounts ? (
          <DataTableSkeleton />
        ) : (
          <DataTable
            title={t('contracts:components.clientAccountsTable.subtitle')}
            data={accounts}
            columns={accountsColumns}
            options={dataTableOptions}
            // info={DataTableErrorHandler(errorProps)}
          />
        )}
      </DialogContent>
      <DialogActions style={{ padding: currentTheme.spacing(2) }}>
        <Button size="small" color="primary" onClick={handleCancel}>
          {t('contracts:components.clientAccountsTable.mainActions.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
